import { useCallback, useEffect, useState } from 'react'
import * as Sentry from '@sentry/nextjs'
import { toastManager } from '@systemeio/components'
import type { AxiosRequestConfig, AxiosResponse } from 'axios'
import { useTranslation } from 'next-i18next/pages'
import { useRouter } from 'next/router'
import { httpClient } from 'shared/api/http-client'
import { regenerateJWT } from 'shared/api/regenerate-jwt'
import { AccessDenied } from 'shared/errors/access-denied'
import { BadRequest } from 'shared/errors/bad-request'
import { CommunityOwnerBlockedError } from 'shared/errors/community-owner-blocked'
import { ConflictError } from 'shared/errors/conflict-error'
import { InternalError } from 'shared/errors/internal-error'
import { LockedError } from 'shared/errors/locked'
import { MaintenanceError } from 'shared/errors/maintenance-error'
import { PayloadTooLarge } from 'shared/errors/payload-too-large'
import { UnauthorizedResponse } from 'shared/errors/unauthorized-response'
import { UnprocessableContentError } from 'shared/errors/unprocessable-content-error'
import type { ErrorResponseInterface } from 'shared/types/error-response-content-interface'
import { InvalidTokenError } from '../../errors/invalid-token-error'
import { NotFound } from '../../errors/not-found'
import type { FetcherType, UseApiWrapperWithErrorValidationProps } from './types'
import { RequestMethodsEnum } from './types'

let isJWTRegenerated = false

export const useApiWrapperWithErrorValidation = <
  M extends RequestMethodsEnum,
  R = void,
  D = unknown,
  BadRequestError = ErrorResponseInterface,
>({
  method,
  showFieldsErrorToast,
  badRequestHandler,
  unauthorizedResponseHandler,
  unprocessableContentHandler,
  conflictResponseHandler,
  afterFetch,
  notFoundResponseHandler,
  accessDeniedResponseHandler,
  lockedResponseHandler,
}: UseApiWrapperWithErrorValidationProps<R, M, BadRequestError>): {
  fetcher: FetcherType<M, R>
  isFetching: boolean
} => {
  const { t } = useTranslation()

  const router = useRouter()

  const [numberOfRequests, setNumberOfRequests] = useState<number>(0)
  const [isFetching, setIsFetching] = useState<boolean>(false)

  useEffect(() => setIsFetching(!!numberOfRequests), [numberOfRequests])

  const fetcher: FetcherType<M, R> = useCallback(
    async (...args) => {
      setNumberOfRequests(numberOfRequests => numberOfRequests + 1)
      return httpClient[method]<R, AxiosResponse<R>, D>(
        args[0],
        args[1] as (D & AxiosRequestConfig<D>) | undefined,
        args[2] as AxiosRequestConfig<D>,
      )
        .then(async res => (res && afterFetch ? await afterFetch(res.data) : res.data))
        .catch(async error => {
          if (error instanceof CommunityOwnerBlockedError) {
            // The modal will already be displayed via the event listener.
            // No toast/redirect/error monitoring, and we don't pass it on.
            return
          }

          if (error instanceof LockedError) {
            lockedResponseHandler?.(error)
            return
          }

          if (globalThis.window !== undefined && error instanceof UnauthorizedResponse) {
            if (unauthorizedResponseHandler) {
              unauthorizedResponseHandler(error)
            } else {
              globalThis.location.assign(error.redirect)
            }
          } else if (error instanceof InvalidTokenError) {
            if (isJWTRegenerated) {
              toastManager.add({ type: 'error', title: t('global.error') })
              Sentry.captureException(error, {
                extra: { context: 'Invalid token error' },
              })
            } else {
              try {
                // NOTE: Generating a new JWT
                await regenerateJWT()
                isJWTRegenerated = true
                // NOTE: repeat the same request
                return fetcher(...args)
              } catch (error) {
                if (error instanceof UnauthorizedResponse) {
                  if (unauthorizedResponseHandler) {
                    unauthorizedResponseHandler(error)
                  } else {
                    globalThis.location.assign(error.redirect)
                  }
                }
              }
            }
          } else if (
            globalThis.window !== undefined &&
            method !== RequestMethodsEnum.get &&
            error instanceof MaintenanceError
          ) {
            toastManager.add({ type: 'error', title: t('core.error.maintenance_error_message') })
          } else if (globalThis.window !== undefined && error instanceof InternalError) {
            toastManager.add({ type: 'error', title: t('core.error.internal_error_message') })
          } else if (badRequestHandler !== null && error instanceof BadRequest) {
            if (badRequestHandler) {
              badRequestHandler(error as unknown as BadRequestError)
            } else {
              const commonError = (error as ErrorResponseInterface)?.errors?.common
              if (commonError && commonError.length > 0) {
                toastManager.add({ type: 'error', title: commonError.join('') })
              }
            }
          } else if (error instanceof ConflictError) {
            if (conflictResponseHandler) {
              conflictResponseHandler(error)
            } else {
              toastManager.add({ type: 'error', title: t('core.error.conflict_error_message') })
            }
          } else if (error instanceof UnprocessableContentError) {
            if (unprocessableContentHandler) {
              unprocessableContentHandler(error)
            } else {
              toastManager.add({ type: 'error', title: t('core.error.conflict_error_message') })
            }
          } else if (error instanceof PayloadTooLarge) {
            toastManager.add({
              type: 'error',
              title: t('core.error.payload_to_large_error_message'),
            })
          } else if (globalThis.window !== undefined && error instanceof AccessDenied) {
            if (accessDeniedResponseHandler) {
              accessDeniedResponseHandler(error)
              return
            }
            const searchParams = error.response?.data?.reason
              ? 'reason=' + error.response?.data?.reason
              : ''
            if (process.env.NODE_ENV === 'development') {
              router.push({
                pathname: '/403',
                search: searchParams,
              })
            } else {
              router.replace({
                pathname: '/403',
                search: searchParams,
              })
            }
          } else if (globalThis.window !== undefined && error instanceof NotFound) {
            if (notFoundResponseHandler) {
              notFoundResponseHandler(error)
            } else {
              if (process.env.NODE_ENV === 'development') {
                router.push('/not-found')
              } else {
                router.replace('/not-found')
              }
            }
          } else if (!(error instanceof AccessDenied)) {
            toastManager.add({ type: 'error', title: t('global.error') })
          }

          throw error
        })
        .finally(() => setNumberOfRequests(numberOfRequests => numberOfRequests - 1))
    },

    // t function causes re-rendering

    [
      method,
      afterFetch,
      router,
      unauthorizedResponseHandler,
      showFieldsErrorToast,
      badRequestHandler,
      unprocessableContentHandler,
      conflictResponseHandler,
      notFoundResponseHandler,
      accessDeniedResponseHandler,
      lockedResponseHandler,
    ],
  ) as FetcherType<M, R>
  return { fetcher, isFetching }
}
